import {FC, useState, useEffect, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {getRecording, updateRecording, deleteRecording, analyzeRecording, SalesRecording} from './core/_requests'

const COMMON_TAGS = [
  'closing',
  'objection handling',
  'cold call',
  'follow-up',
  'intro',
  'demo',
  'negotiation',
  'upsell',
]

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const SalesRecordingDetailPage: FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [recording, setRecording] = useState<SalesRecording | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Editable fields
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [salesperson, setSalesperson] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [rating, setRating] = useState<number | null>(null)
  const [isPublished, setIsPublished] = useState(false)
  const [customTag, setCustomTag] = useState('')
  const [analyzing, setAnalyzing] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)

  const audioRef = useRef<HTMLAudioElement>(null)
  const pollRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    if (id) fetchRecording()
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [id])

  // Poll while processing
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (recording?.ai_status === 'processing') {
      pollRef.current = setInterval(async () => {
        try {
          const data = await getRecording(id!)
          setRecording(data)
          if (data.ai_status !== 'processing') {
            clearInterval(pollRef.current!)
          }
        } catch (err) {
          console.error('Poll failed:', err)
        }
      }, 3000)
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [recording?.ai_status])

  const fetchRecording = async () => {
    setLoading(true)
    try {
      const data = await getRecording(id!)
      setRecording(data)
      setTitle(data.title)
      setDescription(data.description)
      setSalesperson(data.salesperson)
      setTags(data.tags)
      setRating(data.rating)
      setIsPublished(data.is_published)
    } catch (err) {
      console.error('Failed to fetch recording:', err)
    }
    setLoading(false)
  }

  const handleSave = async () => {
    if (!id) return
    setSaving(true)
    try {
      const updated = await updateRecording(id, {
        title,
        description,
        salesperson,
        tags,
        rating,
        is_published: isPublished,
      })
      setRecording(updated)
      alert('Saved successfully')
    } catch (err) {
      alert('Failed to save')
      console.error(err)
    }
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!id || !recording) return
    if (!window.confirm(`Delete "${recording.title}"? This will also delete the audio file.`)) return
    try {
      await deleteRecording(id)
      navigate('/admin/sales-library')
    } catch (err) {
      alert('Failed to delete')
    }
  }

  const handleAnalyze = async () => {
    if (!id) return
    setAnalyzing(true)
    try {
      await analyzeRecording(id)
      setRecording((prev) => prev ? {...prev, ai_status: 'processing' as const} : prev)
    } catch (err) {
      alert('Failed to start analysis')
      console.error(err)
    }
    setAnalyzing(false)
  }

  const toggleTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]))
  }

  const addCustomTag = () => {
    const tag = customTag.trim()
    if (tag && !tags.includes(tag)) {
      setTags((prev) => [...prev, tag])
    }
    setCustomTag('')
  }

  if (loading) {
    return (
      <div style={{padding: '60px', textAlign: 'center'}}>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (!recording) {
    return (
      <div style={{padding: '60px', textAlign: 'center', color: '#5e6278'}}>
        Recording not found
      </div>
    )
  }

  return (
    <div style={{padding: '24px', direction: 'rtl', maxWidth: '800px'}}>
      {/* Back button */}
      <button
        onClick={() => navigate('/admin/sales-library')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          background: 'none',
          border: 'none',
          color: '#3699ff',
          cursor: 'pointer',
          fontSize: '14px',
          marginBottom: '20px',
          padding: 0,
        }}
      >
        <i className='bi bi-arrow-right'></i>
        Back to Library
      </button>

      {/* Audio Player */}
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: '20px',
        }}
      >
        <audio
          ref={audioRef}
          controls
          src={recording.file_url}
          style={{width: '100%', marginBottom: '12px'}}
        />
        <div style={{display: 'flex', gap: '16px', fontSize: '13px', color: '#5e6278'}}>
          <span>
            <i className='bi bi-file-earmark-music' style={{marginLeft: '4px'}}></i>
            {recording.original_filename}
          </span>
          <span>{formatFileSize(recording.file_size)}</span>
          <span>{formatDate(recording.created_at)}</span>
        </div>
      </div>

      {/* AI Analysis */}
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '20px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          marginBottom: '20px',
        }}
      >
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px'}}>
          <h4 style={{margin: 0, fontSize: '16px', fontWeight: 700}}>
            <i className='bi bi-robot' style={{marginLeft: '8px'}}></i>
            AI Analysis
          </h4>
          {recording.ai_status === 'completed' && (
            <span style={{fontSize: '12px', color: '#1bc5bd', display: 'flex', alignItems: 'center', gap: '4px'}}>
              <i className='bi bi-check-circle-fill'></i>
              Analyzed {recording.ai_analyzed_at ? new Date(recording.ai_analyzed_at).toLocaleDateString('he-IL') : ''}
            </span>
          )}
        </div>

        {(!recording.ai_status || recording.ai_status === 'none') && (
          <button
            onClick={handleAnalyze}
            disabled={analyzing}
            style={{
              padding: '10px 20px',
              background: analyzing ? '#a1a5b7' : '#8950fc',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              cursor: analyzing ? 'default' : 'pointer',
              fontWeight: 600,
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <i className='bi bi-stars'></i>
            {analyzing ? 'Starting...' : 'Analyze with AI'}
          </button>
        )}

        {recording.ai_status === 'processing' && (
          <div style={{display: 'flex', alignItems: 'center', gap: '10px', color: '#3699ff', fontSize: '14px'}}>
            <div className='spinner-border spinner-border-sm text-primary' role='status'>
              <span className='visually-hidden'>Processing...</span>
            </div>
            Transcribing and analyzing... This may take a minute.
          </div>
        )}

        {recording.ai_status === 'error' && (
          <div>
            <div style={{color: '#f64e60', fontSize: '14px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px'}}>
              <i className='bi bi-exclamation-triangle-fill'></i>
              Analysis failed: {recording.ai_error}
            </div>
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              style={{
                padding: '8px 16px',
                background: analyzing ? '#a1a5b7' : '#8950fc',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: analyzing ? 'default' : 'pointer',
                fontWeight: 600,
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <i className='bi bi-arrow-clockwise'></i>
              {analyzing ? 'Starting...' : 'Retry Analysis'}
            </button>
          </div>
        )}

        {recording.ai_status === 'completed' && (
          <div>
            {/* Summary */}
            {recording.ai_summary && (
              <div style={{marginBottom: '16px'}}>
                <div style={{fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#1e1e2d'}}>
                  Summary
                </div>
                <div
                  style={{
                    fontSize: '14px',
                    lineHeight: 1.7,
                    color: '#3f4254',
                    background: '#f9f9fc',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {recording.ai_summary}
                </div>
              </div>
            )}

            {/* Sale Outcome */}
            {recording.ai_sale_made !== null && recording.ai_sale_made !== undefined && (
              <div style={{marginBottom: '16px'}}>
                <div style={{fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#1e1e2d'}}>
                  Sale Outcome
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    background: recording.ai_sale_made ? '#e8fff3' : '#fff5f8',
                  }}
                >
                  <i
                    className={`bi bi-${recording.ai_sale_made ? 'check-circle-fill' : 'x-circle-fill'}`}
                    style={{
                      fontSize: '20px',
                      color: recording.ai_sale_made ? '#1bc5bd' : '#f64e60',
                    }}
                  ></i>
                  <span
                    style={{
                      fontWeight: 600,
                      fontSize: '15px',
                      color: recording.ai_sale_made ? '#1bc5bd' : '#f64e60',
                    }}
                  >
                    {recording.ai_sale_made ? 'Sale Made' : 'No Sale'}
                  </span>
                </div>
                {recording.ai_deal_details && (
                  <div
                    style={{
                      marginTop: '8px',
                      fontSize: '14px',
                      lineHeight: 1.7,
                      color: '#3f4254',
                      background: '#f9f9fc',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {recording.ai_deal_details}
                  </div>
                )}
              </div>
            )}

            {/* Key Points */}
            {recording.ai_key_points && recording.ai_key_points.length > 0 && (
              <div style={{marginBottom: '16px'}}>
                <div style={{fontWeight: 600, fontSize: '13px', marginBottom: '6px', color: '#1e1e2d'}}>
                  Key Points
                </div>
                <ul
                  style={{
                    margin: 0,
                    paddingRight: '20px',
                    paddingLeft: 0,
                    listStylePosition: 'inside',
                  }}
                >
                  {recording.ai_key_points.map((point, i) => (
                    <li
                      key={i}
                      style={{
                        fontSize: '14px',
                        lineHeight: 1.8,
                        color: '#3f4254',
                      }}
                    >
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Transcript (collapsible) */}
            {recording.transcript && (
              <div>
                <button
                  onClick={() => setShowTranscript(!showTranscript)}
                  style={{
                    background: 'none',
                    border: '1px solid #e8ecef',
                    borderRadius: '8px',
                    padding: '8px 14px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: '#5e6278',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontWeight: 500,
                  }}
                >
                  <i className={`bi bi-chevron-${showTranscript ? 'up' : 'down'}`}></i>
                  {showTranscript ? 'Hide Transcript' : 'Show Transcript'}
                </button>
                {showTranscript && (
                  <div
                    style={{
                      marginTop: '10px',
                      fontSize: '14px',
                      lineHeight: 1.8,
                      color: '#3f4254',
                      background: '#f9f9fc',
                      padding: '16px',
                      borderRadius: '8px',
                      maxHeight: '400px',
                      overflowY: 'auto',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {recording.transcript}
                  </div>
                )}
              </div>
            )}

            {/* Re-analyze button */}
            <button
              onClick={handleAnalyze}
              disabled={analyzing}
              style={{
                marginTop: '12px',
                padding: '6px 14px',
                background: '#fff',
                color: '#8950fc',
                border: '1px solid #8950fc',
                borderRadius: '8px',
                cursor: analyzing ? 'default' : 'pointer',
                fontSize: '12px',
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <i className='bi bi-arrow-clockwise'></i>
              {analyzing ? 'Starting...' : 'Re-analyze'}
            </button>
          </div>
        )}
      </div>

      {/* Edit Form */}
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        }}
      >
        {/* Title */}
        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
            Title
          </label>
          <input
            type='text'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e8ecef',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Description */}
        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='What can be learned from this call?'
            rows={4}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e8ecef',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'vertical',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Salesperson */}
        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
            Salesperson
          </label>
          <input
            type='text'
            value={salesperson}
            onChange={(e) => setSalesperson(e.target.value)}
            placeholder='Who made this call?'
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #e8ecef',
              borderRadius: '8px',
              fontSize: '14px',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Tags */}
        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
            Tags
          </label>
          <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px'}}>
            {COMMON_TAGS.map((tag) => (
              <button
                key={tag}
                onClick={() => toggleTag(tag)}
                style={{
                  padding: '4px 10px',
                  border: '1px solid',
                  borderColor: tags.includes(tag) ? '#3699ff' : '#e8ecef',
                  borderRadius: '16px',
                  background: tags.includes(tag) ? '#e3f2fd' : '#fff',
                  color: tags.includes(tag) ? '#3699ff' : '#5e6278',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                {tag}
              </button>
            ))}
          </div>
          <div style={{display: 'flex', gap: '8px'}}>
            <input
              type='text'
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addCustomTag()
                }
              }}
              placeholder='Custom tag...'
              style={{
                flex: 1,
                padding: '6px 10px',
                border: '1px solid #e8ecef',
                borderRadius: '8px',
                fontSize: '13px',
              }}
            />
            <button
              onClick={addCustomTag}
              style={{
                padding: '6px 12px',
                border: '1px solid #e8ecef',
                borderRadius: '8px',
                background: '#fff',
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              Add
            </button>
          </div>
          {tags.filter((t) => !COMMON_TAGS.includes(t)).length > 0 && (
            <div style={{display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap'}}>
              {tags
                .filter((t) => !COMMON_TAGS.includes(t))
                .map((tag) => (
                  <span
                    key={tag}
                    style={{
                      fontSize: '11px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: '#e3f2fd',
                      color: '#3699ff',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    {tag}
                    <i
                      className='bi bi-x'
                      style={{cursor: 'pointer'}}
                      onClick={() => toggleTag(tag)}
                    ></i>
                  </span>
                ))}
            </div>
          )}
        </div>

        {/* Rating */}
        <div style={{marginBottom: '16px'}}>
          <label style={{display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '13px'}}>
            Rating
          </label>
          <div style={{display: 'flex', gap: '4px'}}>
            {[1, 2, 3, 4, 5].map((r) => (
              <button
                key={r}
                onClick={() => setRating(rating === r ? null : r)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '24px',
                  color: r <= (rating || 0) ? '#ffa800' : '#e8ecef',
                  padding: '0 2px',
                }}
              >
                ★
              </button>
            ))}
          </div>
        </div>

        {/* Published toggle */}
        <div style={{marginBottom: '24px'}}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            <input
              type='checkbox'
              checked={isPublished}
              onChange={(e) => setIsPublished(e.target.checked)}
              style={{width: '16px', height: '16px'}}
            />
            Published
          </label>
        </div>

        {/* Actions */}
        <div style={{display: 'flex', gap: '10px', justifyContent: 'space-between'}}>
          <div style={{display: 'flex', gap: '10px'}}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '10px 24px',
                background: saving ? '#a1a5b7' : '#3699ff',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: saving ? 'default' : 'pointer',
                fontWeight: 600,
                fontSize: '14px',
              }}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
          <button
            onClick={handleDelete}
            style={{
              padding: '10px 24px',
              background: '#fff',
              color: '#f64e60',
              border: '1px solid #f64e60',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '14px',
            }}
          >
            <i className='bi bi-trash' style={{marginLeft: '6px'}}></i>
            Delete Recording
          </button>
        </div>
      </div>
    </div>
  )
}

export default SalesRecordingDetailPage
